import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { FormProvider, useForm } from 'react-hook-form';
import type { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import { Alert, AlertDescription, AlertTitle } from '@nesso/ui/primitives/alert';
import { Button } from '@nesso/ui/primitives/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@nesso/ui/primitives/dialog';
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@nesso/ui/primitives/form';

import { useRecordingControls, useRecordingStore } from '@/entities/audio-recording';
import {
    generateNotesSchema,
    getMaxSessionDurationMinutes,
    lastSessionOptions,
    MAX_SELECTED_APPROACHES,
    queryKeys,
} from '@/entities/session';
import {
    treatmentApproachesGroupsOrder,
    useTreatmentApproachesOptions,
} from '@/entities/treatment-approach';
import { MultiSelect } from '@/shared/ui/common/multi-select';

import { useConfirmSessionDeleteDialog } from '../../../model/use-confirm-session-delete-dialog';
import { useGenerateNote } from '../../../model/use-generate-note';

export function RecordingCaptureGenerateNoteModal() {
    const { clientId, status, length } = useRecordingStore(
        useShallow((state) => ({
            clientId: state.clientId,
            status: state.status,
            length: state.length,
        })),
    );
    const { resume, abort } = useRecordingControls();
    const openConfirmDeleteDialog = useConfirmSessionDeleteDialog();

    const form = useForm<z.input<typeof generateNotesSchema>>({
        mode: 'onChange',
        resolver: zodResolver(generateNotesSchema),
        defaultValues: {
            treatmentApproaches: [],
        },
    });
    const options = useTreatmentApproachesOptions();

    const { data: lastSession } = useQuery({
        ...lastSessionOptions(clientId),
        enabled: !!clientId,
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: hydrate treatment approaches once when the client's last session resolves, keyed on its id
    useEffect(() => {
        if (lastSession?.treatmentApproaches?.length) {
            form.setValue('treatmentApproaches', lastSession.treatmentApproaches);
        }
    }, [lastSession?.id]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: reset the form only on unmount
    useEffect(() => {
        return () => form.reset();
    }, []);

    const queryClient = useQueryClient();

    useEffect(() => {
        return () => {
            if (!clientId) return;
            void queryClient.invalidateQueries({ queryKey: queryKeys.last(clientId) });
        };
    }, [queryClient, clientId]);

    const preventDismiss = (event: Event) => event.preventDefault();
    const isRecordingLimitReached = length === 'too-long' && status === 'stopped';
    const dialogBehavior = isRecordingLimitReached
        ? {
              showCloseButton: false,
              onInteractOutside: preventDismiss,
              onEscapeKeyDown: preventDismiss,
          }
        : {
              showCloseButton: true,
              onInteractOutside: undefined,
              onEscapeKeyDown: undefined,
          };

    const { generateNote, isLoading } = useGenerateNote(form);

    const handleGenerateNote = () => {
        void form.handleSubmit(() => {
            void generateNote();
        })();
    };

    const handleDeleteSession = () => {
        openConfirmDeleteDialog({ onConfirm: async () => await abort() });
    };

    return (
        <FormProvider {...form}>
            <Dialog
                open={status === 'stopped'}
                onOpenChange={(newOpen) => {
                    if (!newOpen) {
                        resume();
                    }
                }}
                modal
            >
                <DialogContent
                    {...dialogBehavior}
                    className="inset-0 translate-x-0 translate-y-0 max-w-none xs:inset-auto xs:top-[50%] xs:left-[50%] xs:translate-x-[-50%] xs:translate-y-[-50%] w-full h-full xs:h-auto p-0 gap-0 bg-card rounded-none xs:rounded-sm flex flex-col"
                >
                    <div className="flex flex-col flex-1 xs:flex-none">
                        <DialogHeader className="p-6 text-left">
                            <DialogTitle>End recording?</DialogTitle>
                            {isRecordingLimitReached && (
                                <Alert variant="cyan">
                                    <AlertTitle>Session automatically ended</AlertTitle>
                                    <AlertDescription>
                                        {`You hit the ${getMaxSessionDurationMinutes()}-minute recording limit.`}
                                    </AlertDescription>
                                </Alert>
                            )}
                        </DialogHeader>

                        <div className="flex flex-col flex-1 xs:flex-none px-6 bg-card rounded-none xs:rounded-b-lg">
                            <div className="space-y-4">
                                <FormField
                                    control={form.control}
                                    name="treatmentApproaches"
                                    render={({ field, fieldState }) => (
                                        <FormItem className="gap-3">
                                            <FormLabel className="leading-6">
                                                Treatment approach
                                            </FormLabel>
                                            <FormControl>
                                                <MultiSelect
                                                    options={options}
                                                    groupOrder={treatmentApproachesGroupsOrder}
                                                    selected={field.value}
                                                    onChange={field.onChange}
                                                    placeholder=""
                                                    maxSelected={MAX_SELECTED_APPROACHES}
                                                    groupLabel={`Select up to ${MAX_SELECTED_APPROACHES}`}
                                                    className="w-full"
                                                    error={!!fieldState.error}
                                                    disabled={isLoading}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className="mt-auto space-y-2 py-6">
                                <Button
                                    onClick={handleGenerateNote}
                                    className="w-full"
                                    disabled={isLoading}
                                    size="default"
                                    variant="default"
                                >
                                    Generate Note
                                </Button>

                                <Button
                                    onClick={handleDeleteSession}
                                    disabled={isLoading}
                                    variant="ghost"
                                    size="default"
                                    className="w-full text-destructive"
                                >
                                    <Trash2 className="h-4 w-4" />
                                    Delete Session
                                </Button>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </FormProvider>
    );
}
